import { useReducer, useCallback } from 'react'
import type {
  AnimationState,
  AnimationAction,
  TransformState,
  TransitionType,
  TransitionConfig,
  SpringConfig,
  InertiaConfig,
  TargetShape,
} from '../types'
import { DEFAULT_TRANSFORM, DEFAULT_TRANSITION } from '../constants'

const initialState: AnimationState = {
  transform: { ...DEFAULT_TRANSFORM },
  transition: {
    type: DEFAULT_TRANSITION.type,
    spring: { ...DEFAULT_TRANSITION.spring },
    tween: { ...DEFAULT_TRANSITION.tween },
    inertia: { ...DEFAULT_TRANSITION.inertia },
  },
  targetShape: 'box',
  isPlaying: false,
}

function reducer(state: AnimationState, action: AnimationAction): AnimationState {
  switch (action.type) {
    case 'SET_TRANSFORM':
      return { ...state, transform: { ...state.transform, ...action.payload } }
    case 'SET_TRANSITION_TYPE':
      return { ...state, transition: { ...state.transition, type: action.payload } }
    case 'SET_SPRING':
      return {
        ...state,
        transition: {
          ...state.transition,
          spring: { ...state.transition.spring, ...action.payload },
        },
      }
    case 'SET_TWEEN_DURATION':
      return {
        ...state,
        transition: {
          ...state.transition,
          tween: { ...state.transition.tween, duration: action.payload },
        },
      }
    case 'SET_TWEEN_EASE':
      return {
        ...state,
        transition: {
          ...state.transition,
          tween: { ...state.transition.tween, ease: action.payload },
        },
      }
    case 'SET_INERTIA':
      return {
        ...state,
        transition: {
          ...state.transition,
          inertia: { ...state.transition.inertia, ...action.payload },
        },
      }
    case 'SET_TARGET_SHAPE':
      return { ...state, targetShape: action.payload }
    case 'PLAY':
      return { ...state, isPlaying: true }
    case 'STOP':
      return { ...state, isPlaying: false }
    case 'RESET':
      return {
        ...state,
        transform: { ...DEFAULT_TRANSFORM },
        isPlaying: false,
      }
    case 'LOAD_CONFIG':
      return {
        ...state,
        transform: { ...DEFAULT_TRANSFORM, ...action.payload.transform },
        transition: {
          ...state.transition,
          ...(action.payload.transition ? {
            type: action.payload.transition.type ?? state.transition.type,
            spring: { ...state.transition.spring, ...action.payload.transition.spring },
            tween: { ...state.transition.tween, ...action.payload.transition.tween },
            inertia: { ...state.transition.inertia, ...action.payload.transition.inertia },
          } : {}),
        },
        targetShape: action.payload.targetShape ?? state.targetShape,
        isPlaying: false,
      }
    default:
      return state
  }
}

export function useAnimationState() {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setTransform = useCallback(
    (payload: Partial<TransformState>) => dispatch({ type: 'SET_TRANSFORM', payload }),
    []
  )
  const setTransitionType = useCallback(
    (payload: TransitionType) => dispatch({ type: 'SET_TRANSITION_TYPE', payload }),
    []
  )
  const setSpring = useCallback(
    (payload: Partial<SpringConfig>) => dispatch({ type: 'SET_SPRING', payload }),
    []
  )
  const setTweenDuration = useCallback(
    (payload: number) => dispatch({ type: 'SET_TWEEN_DURATION', payload }),
    []
  )
  const setTweenEase = useCallback(
    (payload: string) => dispatch({ type: 'SET_TWEEN_EASE', payload }),
    []
  )
  const setInertia = useCallback(
    (payload: Partial<InertiaConfig>) => dispatch({ type: 'SET_INERTIA', payload }),
    []
  )
  const setTargetShape = useCallback(
    (payload: TargetShape) => dispatch({ type: 'SET_TARGET_SHAPE', payload }),
    []
  )
  const play = useCallback(() => dispatch({ type: 'PLAY' }), [])
  const stop = useCallback(() => dispatch({ type: 'STOP' }), [])
  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])
  const loadConfig = useCallback(
    (payload: { transform?: Partial<TransformState>; transition?: Partial<TransitionConfig>; targetShape?: TargetShape }) =>
      dispatch({ type: 'LOAD_CONFIG', payload }),
    []
  )

  return {
    state,
    setTransform,
    setTransitionType,
    setSpring,
    setTweenDuration,
    setTweenEase,
    setInertia,
    setTargetShape,
    play,
    stop,
    reset,
    loadConfig,
  }
}
